import { api } from "@/api";
import AuthService from "../api/auth-service";
import { Permiso } from "@/shared/types/permiso"; 
import { keepPreviousData, useQuery, useQueryClient } from "@tanstack/react-query";
import { createContext, PropsWithChildren, useContext } from "react";

type LocalData = {
    token: string,
};

type AuthData = {
    id: string,
    email: string,
    nombre: string,
    permisos: Permiso[],
    isTemporal: boolean,
    organizacionId: string,
    organizacionNombre: string,
}

export interface AuthContextReturn { //Datos que retorna el contexto
    authData: AuthData,
    isLoggedIn: boolean,
    isLogingIn: boolean,
    isLoginError: boolean,

    login: (request: { email: string, password: string }) => Promise<AuthData>,
    logout: () => Promise<void>,

    setPassword: (newPassword: string) => Promise<void>,
    updateAuthData: () => Promise<void>,
    hasPermisos: (...permisos: Permiso[]) => boolean
}

const QUERY_AUTH_KEY = "auth";
const LOCAL_STORAGE_AUTH_KEY = "auth";

const AuthContext = createContext<AuthContextReturn>({
    isLoggedIn: false,
    isLoginError: true,
    isLogingIn: false,
    authData: undefined!,

    login: async (_) => { throw Error("No login context found") },
    logout: async () => { },

    setPassword: async (_) => { },
    updateAuthData: async () => { },
    hasPermisos: (_) => false
})

export const useAuthContext = () => {
    return useContext(AuthContext);
}

export const AuthProvider = ({ children }: PropsWithChildren) => {
    const queryClient = useQueryClient();
    const { data: authData, isLoading, isError, refetch } = useQuery({
        queryFn: async () => {
            try {
                //Get local data
                const localData = getLocalData();
                if (!localData) return null;

                //Ask server if logged in
                const response = await AuthService.whoami(localData.token);
                if (!response) {
                    deleteLocalData();
                    return null;
                } else {
                    setLocalData(response);
                    return response;
                }
            } catch (e) {
                deleteLocalData();
                throw e;
            }
        },
        queryKey: [QUERY_AUTH_KEY],
        placeholderData: keepPreviousData
    })

    //Public
    const login = async (request: { email: string, password: string }): Promise<AuthData> => {
        const data = await AuthService.login(request);
        setLocalData(data);
        queryClient.setQueryData([QUERY_AUTH_KEY], data);
        return data;
    }

    const logout = async () => {
        deleteLocalData();
        queryClient.setQueryData([QUERY_AUTH_KEY], null);
        queryClient.removeQueries();
    }

    const setPassword = async (newPassword: string) => {
        await AuthService.setPassword(newPassword);
        await updateAuthData();
    }

    const updateAuthData = async () => {
        await refetch();
    }

    const hasPermisos = (...permisos: Permiso[]) => {
        if (!authData) return false;
        if (authData.isTemporal) return false;
        for (const permiso of permisos) {
            if (!authData.permisos.includes(permiso))
                return false;
        }
        return true;
    }


    //Private
    const getLocalData = () => {
        const stringData = localStorage.getItem(LOCAL_STORAGE_AUTH_KEY);
        if (!stringData) return undefined;
        return JSON.parse(stringData) as LocalData;
    }

    const setLocalData = (data: LocalData) => {
        localStorage.setItem(LOCAL_STORAGE_AUTH_KEY, JSON.stringify(data));
        api.defaults.headers.common["Authorization"] = `Bearer ${data.token}`
    }

    const deleteLocalData = () => {
        localStorage.removeItem(LOCAL_STORAGE_AUTH_KEY);
        delete api.defaults.headers.common["Authorization"];
    }

    return <AuthContext.Provider value={{
        authData: authData!,
        isLoggedIn: Boolean(authData),
        isLoginError: isError,
        isLogingIn: isLoading,

        login: login,
        logout: logout,

        setPassword: setPassword,
        updateAuthData: updateAuthData,
        hasPermisos: hasPermisos
    }}>
        {children}
    </AuthContext.Provider>
}